/* eslint-disable max-lines-per-function */
import { useClickOutside } from '@deltasierra/react-hooks';
import { assertNever } from '@deltasierra/shared';
import { alpha } from '@material-ui/core';
import * as React from 'react';
import styled from 'styled-components';
import { DSPopperProps } from '../../../mui';
import { HelpPopperIconSize, HelpPopperIconStyle, getHelpPopperIcon } from './HelpPopperIcon';
import { HelpPopperPopperMaxWidthType, HelpPopperPopper } from './HelpPopperPopper';
import { useHelpPopperAnchorRef } from './useHelpPopperAnchorRef';

const StyledWrapper = styled.span<{ $active?: boolean; $clickable?: boolean; $margin: HelpPopperMarginType }>`
    display: inline-flex;
    vertical-align: middle;

    border-radius: 50%;

    ${props =>
        props.$active &&
        `
        background: ${alpha(props.theme.palette.info.light, 0.1)};
    `}

    ${({ $margin, theme }) => {
        const spacing = `${theme.spacing(1)}px`;
        switch ($margin) {
            case 'none':
                return '';
            case 'left':
                return `margin-left: ${spacing};`;
            case 'right':
                return `margin-right: ${spacing};`;
            case 'both':
                return `
                    margin-left: ${spacing};
                    margin-right: ${spacing};
                `;
            default:
                throw assertNever($margin);
        }
    }}


    ${props => props.$clickable && 'cursor: pointer;'}
`;

export type HelpPopperMarginType = 'both' | 'left' | 'none' | 'right';

type OptionalIconProps =
    | {
          icon: (active: boolean) => JSX.Element;
          iconSize?: never;
      }
    | {
          icon?: HelpPopperIconStyle;
          iconSize?: HelpPopperIconSize;
      };

type OptionalPopperIconProps =
    | {
          popperIcon: () => JSX.Element;
          popperIconSize?: never;
      }
    | {
          popperIcon?: HelpPopperIconStyle;
          popperIconSize?: HelpPopperIconSize;
      };

export type HelpPopperProps = OptionalIconProps &
    OptionalPopperIconProps & {
        allowHover?: boolean;
        allowToggle?: boolean;
        anchorEle?: DSPopperProps['anchorEl'];
        children: React.ReactNode;
        className?: string;
        margin?: HelpPopperMarginType;
        maxWidth?: HelpPopperPopperMaxWidthType;
        placement?: DSPopperProps['placement'];
        popperOffset?: number;
    };

export function HelpPopper({
    allowHover = true,
    allowToggle = true,
    anchorEle,
    children,
    className,
    icon = 'outline',
    iconSize,
    margin = 'none',
    maxWidth = 'medium',
    placement = 'right',
    popperIcon = 'outline',
    popperIconSize,
    popperOffset,
}: HelpPopperProps): JSX.Element {
    const [helpIconEle, setHelpIconEle] = useHelpPopperAnchorRef();
    const [show, updateShow] = React.useState(false);
    const [forceShow, updateForceShow] = React.useState(false);
    const clickOutsideRef = useClickOutside(() => {
        if (forceShow) {
            updateForceShow(false);
        }
    }, [forceShow]);

    const onMouseEnter = React.useCallback(() => {
        if (allowHover) {
            updateShow(true);
        }
    }, [allowHover, updateShow]);
    const onMouseLeave = React.useCallback(() => {
        if (allowHover) {
            updateShow(false);
        }
    }, [allowHover, updateShow]);
    const toggle = React.useCallback(() => {
        if (allowToggle) {
            updateForceShow(old => !old);
        }
    }, [allowToggle, updateForceShow]);

    const memoIcon = React.useMemo(
        () => (typeof icon === 'string' ? getHelpPopperIcon(icon, iconSize, forceShow) : icon(forceShow)),
        [icon, iconSize, forceShow],
    );
    const memoPopperIcon = React.useMemo(
        () => (typeof popperIcon === 'string' ? getHelpPopperIcon(popperIcon, popperIconSize) : popperIcon),
        [popperIcon, popperIconSize],
    );
    const defaultOffsetMemo = React.useMemo(() => {
        function selectDefaultOffset() {
            if (placement.startsWith('top')) {
                return {
                    val1: 0,
                    val2: 15,
                };
            } else if (placement.startsWith('bottom')) {
                return {
                    val1: 0,
                    val2: 5,
                };
            } else {
                return {
                    val1: -5,
                    val2: 5,
                };
            }
        }
        const defaultOffset = selectDefaultOffset();
        return `${defaultOffset.val1}, ${popperOffset ?? defaultOffset.val2}`;
    }, [placement, popperOffset]);

    const modifiersMemo = React.useMemo(
        () => ({
            offset: { offset: defaultOffsetMemo },
        }),
        [defaultOffsetMemo],
    );

    const isShowing = forceShow || show;

    return (
        <StyledWrapper
            $active={forceShow && (icon === 'filled' || icon === 'outline')}
            $clickable={allowToggle}
            $margin={margin}
            className={className}
            ref={setHelpIconEle}
            onClick={toggle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {memoIcon}
            <HelpPopperPopper
                anchorEle={anchorEle ?? helpIconEle}
                clickOutsideRef={clickOutsideRef}
                icon={memoPopperIcon as any}
                maxWidth={maxWidth}
                modifiers={modifiersMemo}
                open={isShowing}
                placement={placement}
            >
                {children}
            </HelpPopperPopper>
        </StyledWrapper>
    );
}
HelpPopper.displayName = 'HelpPopper';
